import React, { createContext, useContext, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ChatMarkdown from './ChatMarkdown';
import { fencedLines, parseAnswerLayout } from '../utils/answerLayout';

/**
 * The chat answer layout (Lane 1405 v3 Part B, slice L2): Ryan's approved
 * mockup (headline, key figure tiles, warning box, titled tables with a note
 * line, sections, next steps always shown, More detail folded, Basis, Ask next).
 *
 * The parts are read from the answer text by `parseAnswerLayout`, the twin of
 * backend/helpers/answer_layout.py; the text itself is never changed. An answer
 * without a known layout version, or any part that breaks its rule, is drawn by
 * ChatMarkdown exactly as before (fail open). Every piece of markdown goes
 * through the same safe renderer (no raw HTML).
 *
 * While the answer streams (`isDraft`), the finished lines render as parts and
 * the unfinished last line as plain text; follow-up buttons stay disabled until
 * the final answer arrives.
 *
 * Headings are divs with a heading role: index.css forces h1 to h3 sizes with
 * !important at 1440px and less.
 */

const INLINE_ELEMENTS = ['p', 'strong', 'em', 'code', 'a', 'del', 'br', 'span', 'img'];

// Reference-style link definitions ("[policy]: https://...") of the whole answer.
// Each part renders on its own, so every piece gets them (Codex L2-1): a link in
// one section whose definition sits in another still resolves. A definition
// renders nothing itself. A line inside a code fence is a code sample, not a
// definition (Codex L2 r2-1).
const REFERENCE_DEFINITION = /^ {0,3}\[[^\]\n]+\]:[ \t]*\S[^\n]*$/;
export function referenceDefinitions(text) {
  if (typeof text !== 'string') return [];
  const lines = text.split('\n');
  const fenced = fencedLines(lines);
  return lines.filter((line, index) => !fenced.has(index) && REFERENCE_DEFINITION.test(line));
}
const Definitions = createContext('');
const withDefinitions = (markdown, definitions) => (
  definitions ? `${markdown || ''}\n\n${definitions}` : (markdown || ''));

function Markdown({ content }) {
  const definitions = useContext(Definitions);
  return <ChatMarkdown content={withDefinitions(content, definitions)} />;
}

// One line of markdown (a headline, a tile, a title, a note): bold and links
// render; block elements are unwrapped into the line; an image is its alt text,
// inert, as in ChatMarkdown (Codex L2-2).
function Inline({ text }) {
  const definitions = useContext(Definitions);
  return (
    <ReactMarkdown
      remarkPlugins={[[remarkGfm, { singleTilde: false }]]}
      skipHtml
      allowedElements={INLINE_ELEMENTS}
      unwrapDisallowed
      components={{
        p: ({ children }) => <>{children}</>,
        a: ({ node, children, ...props }) => {
          void node;
          return <a {...props} target="_blank" rel="noreferrer noopener">{children}</a>;
        },
        img: ({ alt }) => <span className="hm-chat-markdown-image-alt">{alt || 'Image'}</span>,
      }}
    >
      {withDefinitions(text, definitions)}
    </ReactMarkdown>
  );
}

// The finished lines of a streamed draft, and the unfinished last line.
export function splitDraft(text) {
  const value = typeof text === 'string' ? text : '';
  const cut = value.lastIndexOf('\n');
  return cut < 0 ? ['', value] : [value.slice(0, cut + 1), value.slice(cut + 1)];
}

function Figures({ part }) {
  return (
    <div className="hm-ans-tiles" style={{ '--hm-ans-tiles': part.items.length }}>
      {part.items.map((item, i) => (
        <div className="hm-ans-tile" key={i}>
          <div className="hm-ans-tile-k">{item.label && <Inline text={item.label} />}</div>
          <div className="hm-ans-tile-v"><Inline text={item.value} /></div>
          {item.detail && <div className="hm-ans-tile-s"><Inline text={item.detail} /></div>}
        </div>
      ))}
    </div>
  );
}

function Warning({ part }) {
  return (
    <div className="hm-ans-warn" role="note">
      <strong><Inline text={part.title} /></strong>
      {part.text && <Markdown content={part.text} />}
    </div>
  );
}

function Titled({ part }) {
  return (
    <div className="hm-ans-table">
      {part.title && <div className="hm-ans-table-title"><Inline text={part.title} /></div>}
      <Markdown content={part.table !== null ? part.table : part.markdown} />
      {part.table !== null && part.note && <div className="hm-ans-note"><Inline text={part.note} /></div>}
    </div>
  );
}

function Block({ part }) {
  if (part.kind === 'warning') return <Warning part={part} />;
  if (part.kind === 'titled') return <Titled part={part} />;
  return <Markdown content={part.markdown} />;
}

function MoreDetail({ part }) {
  const folds = part.blocks.filter((b) => b.kind === 'fold');
  const [open, setOpen] = useState(() => new Set());
  const allOpen = folds.length > 0 && open.size === folds.length;
  const toggleAll = () => setOpen(allOpen ? new Set() : new Set(folds.map((_, i) => i)));
  let foldIndex = -1;
  return (
    <section className="hm-ans-sec hm-ans-more">
      <div className="hm-ans-more-head">
        <div className="hm-ans-sec-h" role="heading" aria-level={3}><Inline text={part.title} /></div>
        {folds.length > 1 && (
          <button type="button" className="hm-ans-linkbtn" onClick={toggleAll}>
            {allOpen ? 'Collapse all' : 'Expand all'}
          </button>
        )}
      </div>
      {part.blocks.map((block, i) => {
        if (block.kind !== 'fold') return <Block part={block} key={i} />;
        foldIndex += 1;
        const mine = foldIndex;
        return (
          <details
            className="hm-ans-fold"
            key={i}
            open={open.has(mine)}
            onToggle={(e) => {
              const isOpen = e.currentTarget.open;
              setOpen((prev) => {
                if (prev.has(mine) === isOpen) return prev;
                const next = new Set(prev);
                if (isOpen) next.add(mine); else next.delete(mine);
                return next;
              });
            }}
          >
            <summary><Inline text={block.title} /></summary>
            <div className="hm-ans-fold-body"><Titled part={{ ...block, title: null }} /></div>
          </details>
        );
      })}
    </section>
  );
}

function Section({ part }) {
  if (part.role === 'more_detail') return <MoreDetail part={part} />;
  return (
    <section className={`hm-ans-sec${part.role === 'next_steps' ? ' hm-ans-next' : ''}`}>
      <div className="hm-ans-sec-h" role="heading" aria-level={3}><Inline text={part.title} /></div>
      {part.blocks.map((block, i) => <Block part={block} key={i} />)}
    </section>
  );
}

function Followups({ part, onAsk, disabled }) {
  return (
    <div className="hm-ans-follow" role="group" aria-label={part.title}>
      <span className="hm-ans-follow-k">{part.title}</span>
      {part.items.map((item, i) => (
        <button
          type="button"
          key={i}
          className="hm-ans-chip"
          disabled={disabled || typeof onAsk !== 'function'}
          onClick={() => onAsk(item)}
        >
          {item}
        </button>
      ))}
    </div>
  );
}

function Part({ part, onAsk, followupsDisabled }) {
  switch (part.kind) {
    case 'headline':
      return <div className="hm-ans-headline" role="heading" aria-level={2}><Inline text={part.text} /></div>;
    case 'text':
      return <Markdown content={part.markdown} />;
    case 'figures':
      return <Figures part={part} />;
    case 'section':
      return <Section part={part} />;
    case 'basis':
      return <div className="hm-ans-basis">Basis: <Inline text={part.text} /></div>;
    case 'followups':
      return <Followups part={part} onAsk={onAsk} disabled={followupsDisabled} />;
    default:
      return <Block part={part} />;
  }
}

export default function ChatAnswer({ content, layout, isDraft = false, onAsk = null, disabled = false }) {
  const [done, tail] = isDraft ? splitDraft(content) : [content || '', ''];
  const parsed = parseAnswerLayout(done, layout);
  if (parsed === null) return <ChatMarkdown content={content} isDraft={isDraft} />;
  return (
    <Definitions.Provider value={referenceDefinitions(done).join('\n')}>
      <div className={`hm-ans${isDraft ? ' is-draft' : ''}`} data-answer-layout={parsed.version}>
        {parsed.parts.map((part, i) => (
          <Part part={part} key={i} onAsk={onAsk} followupsDisabled={isDraft || disabled} />
        ))}
        {tail && <div className="hm-ans-tail">{tail}</div>}
      </div>
    </Definitions.Provider>
  );
}
